import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";

export const HIRING_TYPES = [
  { key: "fte", label: "Full Time (FTE)" },
  { key: "contract", label: "Contract" },
];
export const hiringLabel = (k) => (k === "fte" ? "Full Time" : k === "contract" ? "Contract" : k);

const FIELD = "w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none";
const LABEL = "text-xs text-slate-500 uppercase tracking-wider";
const blank = { name: "", country_code: "", hiring_types: ["fte"], accounts: [], service_lines: [] };

function RowList({ title, items, onChange, fields, addLabel, testid }) {
  const set = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className={LABEL}>{title}</span>
        <button type="button" data-testid={`${testid}-add`} onClick={() => onChange([...items, Object.fromEntries(fields.map(f => [f.k, ""]))])}
          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500">
          <Plus className="w-3.5 h-3.5" /> {addLabel}
        </button>
      </div>
      {items.length === 0 && <div className="text-xs text-slate-400 mt-2">None yet.</div>}
      <div className="space-y-2 mt-2">
        {items.map((it, i) => (
          <div key={it.id || i} className="flex gap-2" data-testid={`${testid}-row-${i}`}>
            {fields.map(f => (
              <input key={f.k} data-testid={`${testid}-${f.k}-${i}`} value={it[f.k] || ""} placeholder={f.ph} required={f.required}
                onChange={(e) => set(i, f.k, e.target.value)}
                className={`${FIELD} mt-0 ${f.grow ? "flex-[2]" : "flex-1"} min-w-0`} />
            ))}
            <button type="button" title="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-rose-500 shrink-0">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClientDialog({ client, onClose, onSaved }) {
  const [f, setF] = useState(() => (client ? { ...blank, ...client } : blank));
  const [busy, setBusy] = useState(false);
  const toggleType = (k) => {
    const on = f.hiring_types.includes(k);
    if (on && f.hiring_types.length === 1) return toast.error("Pick at least one hiring type");
    setF({ ...f, hiring_types: on ? f.hiring_types.filter(t => t !== k) : [...f.hiring_types, k] });
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        name: f.name, country_code: f.country_code || null, hiring_types: f.hiring_types,
        accounts: f.accounts.filter(a => a.name.trim()), service_lines: f.service_lines.filter(s => s.name.trim()),
      };
      const { data } = client ? await api.put(`/clients/${client.id}`, body) : await api.post("/clients", body);
      toast.success(client ? "Client updated" : "Client onboarded");
      onSaved(data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="tp-card p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto space-y-5" data-testid="client-dialog">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">{client ? "Edit" : "Onboard"}</div>
            <h2 className="font-display text-2xl font-semibold">{client ? client.name : "New Client"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>Client Name</label>
            <input data-testid="client-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Wipro" className={FIELD} />
          </div>
          <div>
            <label className={LABEL}>Country</label>
            <select data-testid="client-country" value={f.country_code || ""} onChange={(e) => setF({ ...f, country_code: e.target.value })} className={`tp-select ${FIELD}`}>
              <option value="">Select country</option>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <div className={LABEL}>Hiring Type</div>
          <div className="flex gap-2 mt-1.5">
            {HIRING_TYPES.map(t => {
              const on = f.hiring_types.includes(t.key);
              return (
                <button type="button" key={t.key} data-testid={`client-type-${t.key}`} aria-pressed={on} onClick={() => toggleType(t.key)}
                  className={`px-3.5 py-2 text-sm rounded-lg border transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "border-[color:var(--bd-sub)] text-slate-600 hover:border-indigo-400"}`}>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
        <RowList title="Accounts" testid="client-accounts" addLabel="Add account" items={f.accounts} onChange={(accounts) => setF({ ...f, accounts })}
          fields={[{ k: "name", ph: "Account name (e.g. HSBC)", required: true, grow: true }, { k: "location", ph: "Location (e.g. Europe)" }]} />
        <RowList title="Service Lines" testid="client-service-lines" addLabel="Add service line" items={f.service_lines} onChange={(service_lines) => setF({ ...f, service_lines })}
          fields={[{ k: "name", ph: "Service line (e.g. CISS — Cloud, Infrastructure & Security)", required: true, grow: true }]} />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-800">Cancel</button>
          <button data-testid="client-save" type="submit" disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {client ? "Save Changes" : "Onboard Client"}
          </button>
        </div>
      </form>
    </div>
  );
}
